import { AA, bestForeground, contrastRatio, isHex } from "./color.js";
import { brand, deriveScale } from "./palette.js";
import { CONTRAST_PAIRS, SEMANTIC_TOKENS, paguzTheme, } from "./theme.js";
const FOREGROUND_CANDIDATES = [brand.white, brand.ink];
/** Primer color de la lista con contraste ≥ `min` sobre `background`; si ninguno, el mejor. */
function firstReadable(background, candidates, min = AA.text) {
    return (candidates.find((c) => contrastRatio(c, background) >= min) ??
        bestForeground(background, candidates));
}
/**
 * Primer tono de la escala de `color` legible como texto (AA) sobre fondo y zona atenuada.
 * Si el color de la entidad ya cumple, se usa tal cual; si no, se oscurece dentro de su escala.
 */
function readableShade(color, base) {
    const scale = deriveScale(color);
    const candidates = [color, scale[700], scale[800], scale[900], scale[950]];
    return (candidates.find((c) => contrastRatio(c, base.background) >= AA.text && contrastRatio(c, base.muted) >= AA.text) ?? brand.ink);
}
/**
 * Deriva un tema completo y accesible (AA) a partir de los colores de la entidad.
 * Primario y acento también se usan como texto (enlaces, montos), así que si no alcanzan AA
 * sobre blanco se oscurecen al tono más cercano de su escala que sí cumple.
 */
export function createTenantTheme(input) {
    const base = paguzTheme.colors;
    const primary = readableShade(input.primary, base);
    const primaryScale = deriveScale(primary);
    const highlight = readableShade(input.highlight ?? base.highlight, base);
    const highlightScale = deriveScale(highlight);
    const primaryFg = bestForeground(primary, FOREGROUND_CANDIDATES);
    // Superficie inversa: el primario si es oscuro; si no, su paso 900.
    const inverse = primaryFg === brand.white ? primary : primaryScale[900];
    const inverseScale = deriveScale(inverse);
    const colors = {
        ...base,
        primary,
        'primary-foreground': primaryFg,
        secondary: primaryScale[50],
        'secondary-foreground': firstReadable(primaryScale[50], [primary, primaryScale[900]]),
        accent: highlightScale[50],
        'accent-foreground': firstReadable(highlightScale[50], [primary, brand.ink]),
        highlight,
        'highlight-foreground': bestForeground(highlight, FOREGROUND_CANDIDATES),
        'highlight-soft': highlightScale[100],
        'highlight-soft-foreground': firstReadable(highlightScale[100], [
            highlightScale[800],
            highlightScale[900],
        ]),
        inverse,
        'inverse-foreground': bestForeground(inverse, FOREGROUND_CANDIDATES),
        'inverse-muted-foreground': firstReadable(inverse, [
            inverseScale[200],
            inverseScale[100],
            brand.white,
        ]),
        'inverse-accent': firstReadable(inverse, [highlightScale[300], highlightScale[200], brand.white], AA.largeText),
        ring: firstReadable(base.background, [highlight, highlightScale[700]], AA.ui),
        ...input.overrides,
    };
    return {
        name: input.name,
        colors,
        radius: input.radius ?? paguzTheme.radius,
        fonts: { ...paguzTheme.fonts, ...input.fonts },
    };
}
/** Evalúa todos los pares de `CONTRAST_PAIRS` en un tema. */
export function auditContrast(colors) {
    return CONTRAST_PAIRS.map((pair) => {
        const ratio = contrastRatio(colors[pair.foreground], colors[pair.background]);
        const required = AA[pair.use];
        return { ...pair, ratio, required, passes: ratio >= required };
    });
}
/** Lanza un error si algún par no cumple AA (úsalo al registrar el tema de un tenant). */
export function assertAccessibleTheme(theme) {
    const invalid = SEMANTIC_TOKENS.filter((token) => !isHex(theme.colors[token]));
    if (invalid.length > 0) {
        throw new Error(`Tema "${theme.name}": colores inválidos en ${invalid.join(', ')}`);
    }
    const failures = auditContrast(theme.colors).filter((r) => !r.passes);
    if (failures.length > 0) {
        const detail = failures
            .map((f) => `${f.foreground}/${f.background} ${f.ratio.toFixed(2)} < ${f.required}`)
            .join('; ');
        throw new Error(`Tema "${theme.name}" no cumple contraste AA: ${detail}`);
    }
}
export function semanticVariable(token) {
    return `--${token}`;
}
/** Variables CSS de un tema (semánticas, radio y fuentes). Aplícalas en `style` o en `:root`. */
export function themeToCssVariables(theme) {
    const vars = {};
    for (const token of SEMANTIC_TOKENS)
        vars[semanticVariable(token)] = theme.colors[token];
    vars['--radius'] = theme.radius;
    vars['--font-heading'] = theme.fonts.heading;
    vars['--font-body'] = theme.fonts.body;
    return vars;
}
/** Aplica un tema a un elemento (por defecto `document.documentElement`). */
export function applyTheme(theme, element) {
    const target = element ?? document.documentElement;
    for (const [name, value] of Object.entries(themeToCssVariables(theme))) {
        target.style.setProperty(name, value);
    }
    target.dataset.tenant = theme.name;
}
//# sourceMappingURL=tenant.js.map